import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Check, X, Clock, LockKeyhole } from "lucide-react";
import { today as todayISO, formatDate } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { levelDisplayName } from "@/lib/levelLabels";
import { useLookupValues } from "@/lib/platformConfig";
import { DatePicker } from "@/components/ui/calendar";

const STATUS_ICON = {
  present: Check,
  absent: X,
  late: Clock,
  half_day: Clock,
  excused: Check,
};

const STATUS_COLOR = {
  present: "success",
  absent: "error",
  late: "warning",
  half_day: "warning",
  excused: "success",
};

const statusClass = (color, active) => {
  if (!active)
    return "bg-white text-[var(--text-secondary)] border-[var(--subtle)] hover:border-[var(--brand)]";
  const map = {
    success: "bg-[var(--success)] text-white border-[var(--success)]",
    error: "bg-[var(--error)] text-white border-[var(--error)]",
    warning: "bg-[var(--warning)] text-white border-[var(--warning)]",
  };
  return map[color];
};

export default function TeacherAttendance() {
  const { t, language } = useI18n();
  const attendanceLookup = useLookupValues("attendance_statuses");
  const statuses = useMemo(() => {
    const translationMap = {
      present: t("teacher.present"),
      absent: t("teacher.absent"),
      late: t("teacher.late"),
      half_day: t("guardian.half_day"),
      excused: t("guardian.excused"),
    };

    const lookupRows = (attendanceLookup.data?.values || []).filter(
      (row) => row?.metadata?.markable !== false
    );

    return lookupRows.map((row) => {
      const key = row.value;
      return {
        key,
        label: translationMap[key] || row.label,
        icon: STATUS_ICON[key] || Clock,
        color: STATUS_COLOR[key] || "warning",
      };
    });
  }, [attendanceLookup.data?.values, t]);
  const hasMarkableStatuses = statuses.length > 0;
  const [params, setParams] = useSearchParams();
  const [date, setDate] = useState(todayISO());
  const sectionIdParam = params.get("section_id");
  const [sectionId, setSectionId] = useState(sectionIdParam || "");
  const [marks, setMarks] = useState({}); // student_id -> status
  const [submitting, setSubmitting] = useState(false);

  const { data: home } = useQuery({
    queryKey: ["teacher-home"],
    queryFn: async () => unwrap(await api.get("/teacher/home")),
  });

  const sections = useMemo(() => home?.sections || [], [home]);

  useEffect(() => {
    if (!sectionId && sections.length > 0) {
      setSectionId(sections[0].id);
    }
  }, [sections, sectionId]);

  const {
    data: att,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["attendance", sectionId, date],
    queryFn: async () =>
      unwrap(
        await api.get("/attendance", {
          params: { section_id: sectionId, date, session: "full_day" },
        }),
      ),
    enabled: !!sectionId,
  });

  useEffect(() => {
    if (att) {
      const map = {};
      (att.records || []).forEach((r, i) => {
        if (r) map[r.student_id] = r.status;
      });
      setMarks(map);
    }
  }, [att]);

  const students = att?.students || [];
  const markAll = (status) => {
    const m = {};
    students.forEach((s) => (m[s.id] = status));
    setMarks(m);
  };

  const submit = async () => {
    if (!hasMarkableStatuses) {
      toast.error("No attendance statuses configured. Contact Platform Admin.");
      return;
    }
    setSubmitting(true);
    try {
      const defaultStatus = statuses[0].key;
      const records = students.map((s) => ({
        student_id: s.id,
        status: marks[s.id] || defaultStatus,
      }));
      await api.post("/attendance/bulk", {
        section_id: sectionId,
        date,
        session: "full_day",
        records,
      });
      toast.success(
        t("teacher.attendanceMarkedToast", { count: records.length }),
      );
      refetch();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  const stats = statuses.reduce(
    (a, s) => ({
      ...a,
      [s.key]: students.filter((st) => marks[st.id] === s.key).length,
    }),
    {},
  );
  const unmarked =
    students.length - Object.values(stats).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5" data-testid="teacher-attendance">
      <div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">
          {t("teacher.markAttendance")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("teacher.tapOnceSubtitle")}
        </p>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardContent className="p-4 grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-widest text-[var(--text-muted)]">
              {t("teacher.section")}
            </label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger data-testid="section-select" className="h-11 mt-1">
                <SelectValue placeholder={t("teacher.chooseSection")} />
              </SelectTrigger>
              <SelectContent>
                {sections.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {levelDisplayName(s.grade, language)} · {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold tracking-widest text-[var(--text-muted)]">
              {t("teacher.date")}
            </label>
            <DatePicker
              value={date}
              onChange={setDate}
              max={todayISO()}
              testId="date-input"
            />
          </div>
        </CardContent>
      </Card>

      {att?.locked && (
        <div className="flex items-center gap-2 text-[var(--warning)] bg-[var(--warning-bg)] px-4 py-3 rounded-md text-sm">
          <LockKeyhole className="h-4 w-4" /> {t("teacher.lockedWarning")}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {statuses.map((s) => (
          <Button
            key={s.key}
            variant="outline"
            size="sm"
            onClick={() => markAll(s.key)}
            data-testid={`markall-${s.key}`}
            className="border-[var(--subtle)]"
          >
            {t("teacher.allLabel", { label: s.label, count: stats[s.key] })}
          </Button>
        ))}
        {!hasMarkableStatuses && (
          <div className="rounded-md border border-[var(--warning)] bg-[var(--warning-bg)] px-3 py-2 text-xs text-[var(--warning)]">
            No markable attendance statuses configured.
          </div>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="space-y-2">
          {students.map((s, idx) => (
            <div
              key={s.id}
              className="flex items-center gap-3 rounded-md border border-[var(--subtle)] bg-white p-3"
            >
              <div className="h-10 w-10 rounded-full bg-[var(--brand)] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[var(--text-primary)] truncate">
                  {s.first_name} {s.last_name}
                </div>
                <div className="text-xs text-[var(--text-muted)] font-mono-num">
                  {s.admission_no}
                </div>
              </div>
              <div className="flex gap-1.5">
                {statuses.map((st) => (
                  <button
                    key={st.key}
                    onClick={() => setMarks({ ...marks, [s.id]: st.key })}
                    data-testid={`mark-${s.id}-${st.key}`}
                    className={cn(
                      "h-10 w-10 rounded-md border-2 flex items-center justify-center transition-all",
                      statusClass(st.color, marks[s.id] === st.key),
                    )}
                    aria-label={t("teacher.markAria", { label: st.label })}
                  >
                    <st.icon className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                ))}
              </div>
            </div>
          ))}
          {students.length === 0 && (
            <Card className="border-[var(--subtle)]">
              <CardContent className="py-10 text-center text-[var(--text-secondary)]">
                {t("teacher.emptySelectSection")}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {students.length > 0 && (
        <div className="sticky bottom-20 md:bottom-4 z-20">
          <Button
            onClick={submit}
            disabled={submitting || att?.locked || !hasMarkableStatuses}
            data-testid="submit-attendance"
            className="w-full h-14 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-semibold text-base shadow-lg"
          >
            {submitting
              ? t("teacher.saving")
              : t("teacher.saveMarked", {
                  marked: Object.keys(marks).length,
                  total: students.length,
                })}
          </Button>
        </div>
      )}
    </div>
  );
}
